import React, { useState } from 'react';
import { Shield, Users, Lock, Plus } from 'lucide-react';
import { Button } from '../ui/Button';

type Permission = 'read' | 'write' | 'delete' | 'manage';
type Resource = 'properties' | 'clients' | 'users' | 'reports' | 'settings';

interface Role {
  id: string;
  name: string;
  permissions: Record<Resource, Permission[]>;
}

const initialRoles: Role[] = [
  {
    id: '1',
    name: 'Administrateur',
    permissions: {
      properties: ['read', 'write', 'delete', 'manage'],
      clients: ['read', 'write', 'delete', 'manage'],
      users: ['read', 'write', 'delete', 'manage'],
      reports: ['read', 'write', 'delete', 'manage'],
      settings: ['read', 'write', 'delete', 'manage'],
    },
  },
  {
    id: '2',
    name: 'Manager',
    permissions: {
      properties: ['read', 'write', 'delete'],
      clients: ['read', 'write', 'delete'],
      users: ['read'],
      reports: ['read', 'write'],
      settings: ['read'],
    },
  },
  {
    id: '3',
    name: 'Agent',
    permissions: {
      properties: ['read', 'write'],
      clients: ['read', 'write'],
      users: ['read'],
      reports: ['read'],
      settings: ['read'],
    },
  },
];

const resources: { id: Resource; label: string }[] = [
  { id: 'properties', label: 'Biens immobiliers' },
  { id: 'clients', label: 'Clients' },
  { id: 'users', label: 'Utilisateurs' },
  { id: 'reports', label: 'Rapports' },
  { id: 'settings', label: 'Paramètres' },
];

const permissions: { value: Permission; label: string }[] = [
  { value: 'read', label: 'Lecture' },
  { value: 'write', label: 'Écriture' },
  { value: 'delete', label: 'Suppression' },
  { value: 'manage', label: 'Gestion complète' },
];

export const PermissionsManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>(initialRoles);
  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const handlePermissionToggle = (
    roleId: string,
    resource: Resource,
    permission: Permission
  ) => {
    setRoles(prevRoles =>
      prevRoles.map(role => {
        if (role.id === roleId) {
          const currentPermissions = role.permissions[resource];
          const newPermissions = currentPermissions.includes(permission)
            ? currentPermissions.filter(p => p !== permission)
            : [...currentPermissions, permission];
          
          return {
            ...role,
            permissions: {
              ...role.permissions,
              [resource]: newPermissions,
            },
          };
        }
        return role;
      })
    );
  };

  const handleAddRole = () => {
    if (!newRoleName.trim()) return;

    const newRole: Role = {
      id: Math.random().toString(36).substr(2, 9),
      name: newRoleName.trim(),
      permissions: {
        properties: [],
        clients: [],
        users: [],
        reports: [],
        settings: [],
      },
    };

    setRoles(prev => [...prev, newRole]);
    setNewRoleName('');
    setShowNewRoleForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary-500" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Gestion des permissions
          </h2>
        </div>
        <Button
          onClick={() => setShowNewRoleForm(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Nouveau rôle
        </Button>
      </div>

      {showNewRoleForm && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Nom du rôle
              </label>
              <input
                type="text"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                className="mt-1"
                placeholder="Ex: Responsable commercial"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setShowNewRoleForm(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddRole}>
                Créer
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Ressource
                </th>
                {roles.map(role => (
                  <th key={role.id} scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {role.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {resources.map(resource => (
                <tr key={resource.id}>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {resource.label}
                      </span>
                    </div>
                  </td>
                  {roles.map(role => (
                    <td key={role.id} className="whitespace-nowrap px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {permissions.map(permission => (
                          <label
                            key={permission.value}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1 dark:border-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={role.permissions[resource.id].includes(permission.value)}
                              onChange={() => handlePermissionToggle(role.id, resource.id, permission.value)}
                              className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              {permission.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-primary-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Permissions spécifiques
          </h3>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Vous pouvez également définir des permissions spécifiques pour certains utilisateurs, qui s'ajouteront à celles de leur rôle.
        </p>
        <Button variant="secondary" className="mt-4">
          Gérer les permissions spécifiques
        </Button>
      </div>
    </div>
  );
};